import {
    getCurrentTagGroups,
    getSelectedGroup,
    getTagsAndCampaignsByGroupSlug,
    isTagGroupSelected,
} from '@domain/segments';
import { getCampaigns } from '@domain/campaigns';
import { isDataLoading } from '@shared/axios';
import { getClientClusters, getClustersState } from '@domain/omnichannel-budget-manager/clusters';
import { getObjectSelectedData, getTypeOfSelectedObject } from '@domain/object-selected';
import { getDashboardFilters, getEndDateFromDate, getStartDateFromDate } from '@domain/dashboard/dashboard-filters';
import { getEntityId } from '@shared/utils';
import { format } from '@shared/wrappers/date/date';
import { API_DATE_FORMAT } from '@shared/utils/date';

export enum GroupFeatures {
    OBM = 'omnichannel-budget-manager',
    SEGMENTS = 'segments',
}

export const getFeatureForSelectedGroup = (selectedGroup): GroupFeatures => {
    const isBudgetManager = selectedGroup.name === 'Budget Manager Automation';
    return isBudgetManager ? GroupFeatures.OBM : GroupFeatures.SEGMENTS;
};

const commonDependenciesAlreadyLoaded = (state) => {
    const selectedGroup = getSelectedGroup(state);
    const campaigns = getCampaigns(state);
    const isCampaignsLoading = isDataLoading(campaigns.loadState);
    const isSelectedGroupBeingEdited = selectedGroup.isCreating;
    const hasCampaignsItems = !!Object.keys(campaigns.data);

    return isTagGroupSelected(selectedGroup.slug)
        && !isSelectedGroupBeingEdited
        && hasCampaignsItems
        && !isCampaignsLoading;
};

export const shouldFetchReport = (state): boolean => {
    const selectedGroup = getSelectedGroup(state);
    const feature = getFeatureForSelectedGroup(selectedGroup);

    if (feature === GroupFeatures.OBM) {
        const clustersState = getClustersState(state);
        const areClustersLoading = isDataLoading(clustersState);

        return commonDependenciesAlreadyLoaded(state) && !areClustersLoading;
    }

    if (feature === GroupFeatures.SEGMENTS) {
        const currentSegments = getCurrentTagGroups(state);
        const areTagGroupsLoading = isDataLoading(currentSegments.loadState);

        return commonDependenciesAlreadyLoaded(state) && !areTagGroupsLoading;
    }

    return false;
};

const buildSegmentsGroups = (state) => {
    const selectedGroup = getSelectedGroup(state);
    const objectSelected = getObjectSelectedData(state);

    const campaigns = getCampaigns(state);
    const tagsAndCampaigns = getTagsAndCampaignsByGroupSlug(
        selectedGroup, campaigns.data, objectSelected.account_selected,
    );

    return Object.keys(tagsAndCampaigns).map(tag => ({
        tag,
        groupSlug: selectedGroup.slug,
        campaigns: tagsAndCampaigns[tag],
    }));
};

const buildClustersGroups = (state) => {
    const selectedGroup = getSelectedGroup(state);
    const clustersData = getClientClusters(state)?.data;

    return clustersData.map(cluster => ({
        tag: cluster.name,
        groupSlug: selectedGroup.slug,
        campaigns: cluster.clusterObjects
            .map(
                ({ objectId, platform }) => ({
                    campaign_id: objectId,
                    platform,
                }),
            ),
    }));
};

export const getTagGroupSegmentedReportParams = (state) => {
    const dashboardFilters = getDashboardFilters(state);
    const objectSelected = getObjectSelectedData(state);
    const customerId = getEntityId(objectSelected);
    const customerType = getTypeOfSelectedObject(objectSelected);
    const { conversionType, segmentation } = dashboardFilters;

    const selectedGroup = getSelectedGroup(state);
    const feature = getFeatureForSelectedGroup(selectedGroup);

    return {
        startDate: format(getStartDateFromDate(dashboardFilters), API_DATE_FORMAT),
        endDate: format(getEndDateFromDate(dashboardFilters), API_DATE_FORMAT),
        customerType,
        customerId,
        segmentation,
        conversionType,
        groups: feature === GroupFeatures.OBM
            ? buildClustersGroups(state) : buildSegmentsGroups(state),
    };
};
